import { useParams, Link } from "react-router-dom";
import { articles, siteInfo } from "@/mocks/videoData";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-article-line text-5xl text-foreground-400 mb-4 block"></i>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb items={[{ name: "文章", path: "/" }, { name: article.title }]} />
          </div>
        </div>

        <article className="py-8 md:py-12 bg-background-50" itemScope itemType="https://schema.org/Article">
          <div className="max-w-[860px] mx-auto px-4 md:px-6">
            {/* 文章头部 */}
            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight" itemProp="headline">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                <span className="flex items-center gap-1.5" itemProp="author" itemScope itemType="https://schema.org/Person">
                  <i className="ri-user-line"></i>
                  <span itemProp="name">{article.author}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  <time dateTime={article.publishDate} itemProp="datePublished">{article.publishDate}</time>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views} 阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {article.tags.map((tag) => (
                  <span key={tag} className="bg-secondary-100 text-secondary-900 text-xs px-3 py-1 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <img
                src={article.cover}
                alt={article.title}
                title={article.title}
                className="w-full aspect-video object-cover rounded-lg"
                itemProp="image"
              />
            </header>

            {/* 文章正文 */}
            <div
              className="prose prose-foreground max-w-none mb-10"
              itemProp="articleBody"
            >
              {article.content.split("\n").map((paragraph, i) => {
                if (paragraph.startsWith("## ")) {
                  return (
                    <h2 key={i} className="text-xl font-bold text-foreground-950 mt-8 mb-4">
                      {paragraph.replace("## ", "")}
                    </h2>
                  );
                }
                if (paragraph.startsWith("- ")) {
                  return (
                    <li key={i} className="text-foreground-700 ml-6 mb-1 text-base leading-relaxed list-disc">
                      {paragraph.replace("- ", "")}
                    </li>
                  );
                }
                if (paragraph.trim() === "") return <br key={i} />;
                if (/^\d+\.\s/.test(paragraph)) {
                  return (
                    <li key={i} className="text-foreground-700 ml-6 mb-1 text-base leading-relaxed list-decimal">
                      {paragraph.replace(/^\d+\.\s/, "")}
                    </li>
                  );
                }
                return (
                  <p key={i} className="text-foreground-700 mb-4 leading-relaxed text-base">
                    {paragraph}
                  </p>
                );
              })}
            </div>

            {/* 相关文章 */}
            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200 pt-8 mt-8">
                <h3 className="text-lg font-bold text-foreground-950 mb-4">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-all"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img src={ra.cover} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h4>
                        <span className="text-xs text-foreground-500 mt-2 block">{ra.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: siteInfo.url,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: article.title,
                    item: `${siteInfo.url}/article/${article.slug}`,
                  },
                ],
              },
              {
                "@type": "Article",
                headline: article.title,
                author: {
                  "@type": "Person",
                  name: article.author,
                },
                datePublished: article.publishDate,
                image: article.cover,
                url: `${siteInfo.url}/article/${article.slug}`,
                publisher: {
                  "@type": "Organization",
                  name: siteInfo.name,
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}